import type { ChildPassenger, ChildCost, ChildAgeBand } from "../../../web/src/lib/childPricing";
import type { PortalAgeSelection } from "../components/PortalActivityAgeRates";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { portalRequest } from "../lib/auth";

export type PortalInventoryType = "hotel" | "activity" | "transport";

export type PortalRoomOption = {
  roomType: string;
  paxPerRoom: number;
  mealPlans: string[];
};

export type PortalVehicleOption = {
  vehicleType: string;
  pax: number;
};

export type PortalInventoryItem = {
  id: string;
  type: PortalInventoryType;
  name: string;
  image?: string | null;
  country: string;
  state?: string | null;
  city: string;
  description?: string;
  inclusions: string[];
  exclusions: string[];
  category?: string;
  childAgeBands?: ChildAgeBand[];
  serviceType?: string;
  rateBasis?: "fixed" | "hourly" | "daily";
  duration?: number | null;
  roomOptions?: PortalRoomOption[];
  vehicleOptions?: PortalVehicleOption[];
};

export type PortalInventoryPage = {
  items: PortalInventoryItem[];
  pagination: PortalPagination;
};

export type PortalInventoryFilters = {
  countries?: string[];
  search?: string;
  city?: string;
  category?: string;
  serviceType?: string;
};

/** Resolves the private transports linked to activities, keyed by activity id. */
export const resolvePortalActivityLinkedTransports = async (activityIds: string[]) => {
  const ids = Array.from(new Set(activityIds.filter(Boolean)));
  const linked = new Map<string, PortalInventoryItem[]>();
  for (let index = 0; index < ids.length; index += 100) {
    const response = await portalRequest<{ items: Array<{ activityId: string; transports: PortalInventoryItem[] }> }>({
      url: "/api/agent-portal/inventory/linked-transports/resolve",
      method: "post",
      data: { activityIds: ids.slice(index, index + 100) },
    });
    response.data.items.forEach((item) => linked.set(item.activityId, item.transports));
  }
  return linked;
};

/** Loads private transports linked to the given activities so the builder can offer SIC / Private. */
export const usePortalLinkedTransportsQuery = (activityIds: string[]) => {
  const sorted = Array.from(new Set(activityIds.filter(Boolean))).sort();
  return useQuery({
    queryKey: ["agent-portal", "linked-transports", sorted],
    queryFn: () => resolvePortalActivityLinkedTransports(sorted),
    enabled: sorted.length > 0,
    staleTime: 5 * 60_000,
  });
};

/**
 * Resolves specific portal-visible inventory items by id, in batches of 150.
 * Used to label saved drafts whose products are not in the first page of destination inventory.
 */
export const resolvePortalInventoryItems = async (
  refs: Array<{ type: PortalInventoryType; id: string }>,
): Promise<PortalInventoryItem[]> => {
  const unique = Array.from(new Map(refs.filter((ref) => ref.id).map((ref) => [`${ref.type}:${ref.id}`, ref])).values());
  const batches = Array.from({ length: Math.ceil(unique.length / 150) }, (_, index) =>
    unique.slice(index * 150, (index + 1) * 150),
  );
  const responses = await Promise.all(
    batches.map((batch) =>
      portalRequest<{ items: PortalInventoryItem[] }>({
        url: "/api/agent-portal/inventory/resolve",
        method: "post",
        data: { items: batch },
      }),
    ),
  );
  return responses.flatMap((response) => response.data.items);
};

/** Resolves optional portal-safe activities configured for selected transports. */
export const resolvePortalTransportRecommendedActivities = async (transportIds: string[]) => {
  const ids = Array.from(new Set(transportIds.filter(Boolean)));
  if (!ids.length) return new Map<string, PortalInventoryItem[]>();
  const batches = Array.from({ length: Math.ceil(ids.length / 50) }, (_, index) => ids.slice(index * 50, (index + 1) * 50));
  const responses = await Promise.all(batches.map((batch) => portalRequest<{
    items: Array<{ transportId: string; activities: PortalInventoryItem[] }>;
  }>({
    url: "/api/agent-portal/inventory/recommended-activities/resolve",
    method: "post",
    data: { transportIds: batch },
  })));
  return new Map(responses.flatMap((response) => response.data.items).map((item) => [item.transportId, item.activities]));
};

export type PortalItineraryPayload = {
  startDate: string;
  nights: number;
  destinations: string[];
  adults: number;
  cwb: number;
  cnb: number;
  infant: number;
  childPassengers?: ChildPassenger[];
  comments?: string;
  conversionCurrency?: string;
  conversionRate?: number;
  currencyRate?: number;
  hotels: Array<{
    inventoryId: string;
    checkInDate: string;
    checkOutDate: string;
    roomType: string;
    mealPlan: string;
    numberOfRooms: number;
    aweb: number;
    cweb: number;
    cnb: number;
    infants: number;
  }>;
  days: Array<{
    day: number;
    activities: Array<{
      inventoryId: string;
      /** Position among the day's transports and activities; unset keeps the default order. */
      dayOrder?: number;
      itineraryItemKey?: string;
      attendance?: { adults: number; children: number; infants: number };
      ageRateSelections?: PortalAgeSelection[];
      agePassengerSnapshot?: string;
      agePricingRequestId?: string;
      reapplyAgePricing?: boolean;
      quantity: number;
      selectedVehicleType?: string | null;
    }>;
    transports: Array<{
      inventoryId: string;
      /** Position among the day's transports and activities; unset keeps the default order. */
      dayOrder?: number;
      quantity: number;
      selectedVehicleType?: string | null;
      startTime?: string | null;
      durationHours?: number | null;
      startDate?: string | null;
      endDate?: string | null;
    }>;
  }>;
};

export type PortalItineraryDefaults = {
  inclusions: string[];
  exclusions: string[];
  currency: string;
  /** Currency agents quote in; the server prefers USD when the tenant has a USD rate. */
  defaultSellingCurrency?: string;
  sellingCurrencies: Array<{ currency: string; rate: number }>;
};

export type PortalStatus<Key extends string> = {
  key: Key;
  label: string;
};

export type PortalTravellerSummary = {
  adults: number;
  cwb: number;
  cnb: number;
  infants: number;
  total: number;
  summary: string;
};

export type PortalOptionStatus = "draft" | "submitted" | "confirmed" | "cancelled";
export type PortalTripRequestStatus = "draft" | "returned" | "submitted" | "confirmed" | "cancelled";
export type PortalBookingStatus = "pending" | "in_progress" | "completed" | "cancelled";

export type PortalItineraryListItem = {
  id: string;
  reference: string;
  destinations: string[];
  startDate: string | null;
  endDate: string | null;
  nights: number;
  travellers: PortalTravellerSummary;
  totalPrice: {
    amount: number;
    currency: string;
  };
  requestStatus: PortalStatus<PortalOptionStatus>;
  bookingStatus: PortalStatus<PortalBookingStatus>;
  submittedAt: string | null;
  updatedAt: string | null;
  downloadAvailable: boolean;
  leadId: string;
  guestName: string;
  optionNumber: number;
  isSubmittedOption: boolean;
  canEdit: boolean;
  canSubmit: boolean;
  portalReturnReason: string | null;
  portalReturnedAt: string | null;
};

export type PortalItineraryListFilters = {
  page?: number;
  limit?: number;
  search?: string;
  status?: PortalOptionStatus;
  bookingStatus?: PortalBookingStatus;
  /** `bookings` limits the list to confirmed quotes and started bookings; the server enforces it. */
  scope?: "bookings";
  destination?: string;
  startDateFrom?: string;
  startDateTo?: string;
  submittedFrom?: string;
  submittedTo?: string;
  sortBy?: "reference" | "guestName" | "destination" | "startDate" | "requestStatus" | "bookingStatus" | "submittedAt" | "updatedAt";
  sortOrder?: "ASC" | "DESC";
};

export type PortalPagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
};

export type PortalPublicItem = {
  id: string | null;
  name: string;
  city: string | null;
  country: string | null;
  state?: string | null;
  image: string | null;
  description: string | null;
  inclusions: string[];
  exclusions: string[];
  operationalStatus: PortalStatus<PortalBookingStatus> | null;
};

export type PortalHotelDetail = PortalPublicItem & {
  category: string | null;
  checkInDate: string | null;
  checkOutDate: string | null;
  roomType: string | null;
  mealPlan: string | null;
  numberOfRooms: number;
  paxPerRoom: number;
  adultExtraBeds: number;
  childExtraBeds: number;
  childrenWithoutBed: number;
  infants: number;
};

export type PortalTransportDetail = PortalPublicItem & {
  /** Position in the day set in the daily plan; absent keeps the default grouping. */
  dayOrder?: number;
  serviceType: string | null;
  quantity: number;
  startTime: string | null;
  durationHours: number | null;
  selectedVehicleType: string | null;
  vehiclePax: number | null;
};

export type PortalActivityDetail = PortalPublicItem & {
  /** Position in the day set in the daily plan; absent keeps the default grouping. */
  dayOrder?: number;
  attendance?: { adults: number; children: number; infants: number };
  category: string | null;
  duration: number | null;
  quantity: number;
  selectedVehicleType: string | null;
  vehiclePax: number | null;
};

export type PortalItineraryDetail = PortalItineraryListItem & {
  travel?: Array<{ source: string; destination: string; airline: string; flightNumber: string; connections: Array<{ source: string; destination: string; airline: string; flightNumber: string }> }>;
  optionalOffers?: Array<{ key: string; name: string; priceLabel: string; description: string; amount: number; currency: string }>;

  customServices?: Array<{ name: string; category: string; timing: string; location: string; description: string; fields: Array<{ label: string; value: string }>; inclusions: string[]; exclusions: string[] }>;
  requestNotes: string | null;
  hotels: PortalHotelDetail[];
  days: Array<{
    day: number;
    date: string | null;
    transports: PortalTransportDetail[];
    activities: PortalActivityDetail[];
  }>;
};

export type PortalItinerarySubmission = {
  id: string;
  leadId?: string;
  reference: string;
};

export type PortalTripRequestSummary = {
  childPassengers?: ChildPassenger[];
  passengerCounts?: { adults: number; cwb: number; cnb: number; infant: number };
  id: string;
  reference: string;
  guestName: string;
  requestStatus: PortalStatus<PortalTripRequestStatus>;
  optionCount: number;
  totalOptionCount: number;
  submittedItineraryId: string | null;
  portalSubmittedAt: string | null;
  portalReturnedAt: string | null;
  portalReturnReason: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  canEdit: boolean;
  canCreateOption: boolean;
  canDiscard: boolean;
  summary: Pick<PortalItineraryListItem, "destinations" | "startDate" | "endDate" | "nights" | "travellers" | "totalPrice"> | null;
};

export type PortalTripRequestDetail = PortalTripRequestSummary & {
  options: PortalItineraryListItem[];
};

export type PortalTripRequestFilters = {
  page?: number;
  limit?: number;
  search?: string;
  status?: PortalTripRequestStatus;
  sortOrder?: "ASC" | "DESC";
};

export type PortalTripRequestDashboard = {
  counts: Record<PortalTripRequestStatus, number>;
  recent: PortalTripRequestSummary[];
  returned: PortalTripRequestSummary[];
};

export type PortalTemplateSummary = {
  id: string;
  name: string;
  destinations: string[];
  nights: number;
  routeSegments: Array<{
    destination: string;
    nights: number;
    cityStops?: Array<{ city: string; nights: number }>;
  }> | null;
  /** City stops in itinerary order: saved on the template or derived from all of its hotels. */
  cityStops?: Array<{ destination: string; city: string; nights: number }>;
  /** `none` means the city stops could not be determined, so the template is not offered per city. */
  cityStopsSource?: "explicit" | "hotels" | "none";
  hotelCount: number;
  activityCount: number;
  transportCount: number;
  dailyTransportCount: number;
};

export type PortalRecommendationRouteStop = {
  destination: string;
  city?: string;
  nights?: number;
};

export type PortalTemplateRecommendation = PortalTemplateSummary & {
  routeIndex?: number;
  dayOffset?: number;
  matchedCity?: string;
  matchedNights?: number;
};

export type PortalTemplateRecommendationPlan = {
  role: "best_fit" | "most_popular" | "simplest" | "alternative";
  templates: PortalTemplateRecommendation[];
  totalNights: number;
  unfilledNights: number;
  reasons: Array<{
    code: string;
    label: string;
  }>;
};

export type PortalTemplateRecommendationResult = {
  plans: PortalTemplateRecommendationPlan[];
  requestedRoute: PortalRecommendationRouteStop[];
  matchMode: "exact" | "one_night_open" | "partial" | "none";
  missingDestinations: string[];
};

export type PortalTemplateDetailItem = PortalInventoryItem & {
  day?: number;
  sequence?: number;
  nights?: number;
  quantity?: number;
  roomType?: string;
  mealPlan?: string;
};

export type PortalTemplateDetail = PortalTemplateSummary & {
  hotels: PortalTemplateDetailItem[];
  activities: PortalTemplateDetailItem[];
  transports: PortalTemplateDetailItem[];
};

export type PortalTemplatePlan = {
  destinations: string[];
  nights: number;
  hotels: Array<{
    hotelId: string;
    nights: number;
    mealPlan: string;
    roomType: string;
    numberOfRooms?: number;
    dayOffset: number;
    inventory: PortalInventoryItem;
  }>;
  days: Array<{
    day: number;
    activities: Array<{
      activityId: string;
      quantity?: number;
      selectedVehicleType?: string | null;
      day: number;
      sequence?: number;
      dayOrder?: number;
      inventory: PortalInventoryItem;
    }>;
    transports: Array<{
      transportId: string;
      quantity?: number;
      selectedVehicleType?: string | null;
      startTime?: string | null;
      durationHours?: number | null;
      day: number;
      dayOrder?: number;
      inventory: PortalInventoryItem;
    }>;
  }>;
  dailyTransports: Array<{
    transportId: string;
    quantity?: number;
    selectedVehicleType?: string | null;
    startDay: number;
    endDay: number;
    inventory: PortalInventoryItem;
  }>;
};

export type PortalTemplateApplication = {
  templateIds: string[];
  targetNights?: number;
  dayOffsets?: number[];
  destinations?: string[];
};

export const portalItineraryKeys = {
  all: ["agent-portal", "itineraries"] as const,
  lists: () => [...portalItineraryKeys.all, "list"] as const,
  list: (filters: PortalItineraryListFilters) => [...portalItineraryKeys.lists(), filters] as const,
  detail: (id: string) => [...portalItineraryKeys.all, "detail", id] as const,
  status: (id: string) => [...portalItineraryKeys.all, "status", id] as const,
};

export const portalTripRequestKeys = {
  all: ["agent-portal", "trip-requests"] as const,
  lists: () => [...portalTripRequestKeys.all, "list"] as const,
  list: (filters: PortalTripRequestFilters) => [...portalTripRequestKeys.lists(), filters] as const,
  detail: (id: string) => [...portalTripRequestKeys.all, "detail", id] as const,
  dashboard: () => [...portalTripRequestKeys.all, "dashboard"] as const,
};

/**
 * Fetches portal-safe inventory without confidential cost or supplier fields.
 * @param type Inventory kind
 * @param countries Selected destination countries used to scope results
 */
export const usePortalInventoryQuery = (type: PortalInventoryType, countries: string[] = []) => {
  const normalizedCountries = Array.from(new Set(countries.map((country) => country.trim()).filter(Boolean))).sort(
    (left, right) => left.localeCompare(right),
  );
  return useQuery({
    queryKey: ["agent-portal", "inventory", type, normalizedCountries],
    queryFn: async () => {
      const response = await portalRequest<PortalInventoryPage>({
        url: "/api/agent-portal/inventory",
        method: "get",
        params: {
          type,
          limit: 50,
          countries: normalizedCountries.length ? normalizedCountries.join(",") : undefined,
        },
      });
      return response.data.items;
    },
    staleTime: 60_000,
  });
};

/** Fetches portal inventory in 50-record pages for the browse modal. */
export const useInfinitePortalInventoryQuery = (type: PortalInventoryType, filters: PortalInventoryFilters, enabled = true) => {
  const normalizedCountries = Array.from(new Set((filters.countries || []).map((country) => country.trim()).filter(Boolean))).sort(
    (left, right) => left.localeCompare(right),
  );
  const query = useInfiniteQuery({
    queryKey: ["agent-portal", "inventory", "infinite", type, normalizedCountries, filters.search, filters.city, filters.category, filters.serviceType],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<PortalInventoryPage> => {
      const response = await portalRequest<PortalInventoryPage>({
        url: "/api/agent-portal/inventory",
        method: "get",
        params: {
          type,
          page: pageParam,
          limit: 50,
          countries: normalizedCountries.length ? normalizedCountries.join(",") : undefined,
          search: filters.search?.trim() || undefined,
          city: filters.city,
          category: type === "transport" ? undefined : filters.category,
          serviceType: type === "transport" ? filters.serviceType : undefined,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => (lastPage.pagination.hasNext ? lastPage.pagination.page + 1 : undefined),
    enabled,
    staleTime: 60_000,
  });
  return { ...query, items: query.data?.pages.flatMap((page) => page.items) || [] };
};

/**
 * Fetches destination countries from the server location catalogue.
 * @param search Optional country-name search
 */
export const usePortalCountriesQuery = (search = "") =>
  useQuery({
    queryKey: ["agent-portal", "countries", search.trim().toLowerCase()],
    queryFn: async () =>
      (
        await portalRequest<{ items: string[] }>({
          url: "/api/agent-portal/countries",
          method: "get",
          params: { search: search.trim() || undefined },
        })
      ).data.items,
    staleTime: 5 * 60_000,
  });

/**
 * Fetches city options for one destination country.
 * @param country Selected destination country
 * @param search Optional city-name search
 */
export const usePortalCitiesQuery = (country: string, search = "") =>
  useQuery({
    queryKey: ["agent-portal", "cities", country.trim().toLowerCase(), search.trim().toLowerCase()],
    queryFn: async () =>
      (
        await portalRequest<{ items: string[] }>({
          url: `/api/agent-portal/countries/${encodeURIComponent(country)}/cities`,
          method: "get",
          params: { search: search.trim() || undefined },
        })
      ).data.items,
    enabled: Boolean(country.trim()),
    staleTime: 5 * 60_000,
  });

/** Fetches the tenant's portal-safe itinerary defaults. */
export const usePortalItineraryDefaultsQuery = () =>
  useQuery({
    queryKey: ["agent-portal", "itinerary-defaults"],
    queryFn: async () =>
      (
        await portalRequest<PortalItineraryDefaults>({
          url: "/api/agent-portal/itinerary-defaults",
          method: "get",
        })
      ).data,
    staleTime: 5 * 60_000,
  });

/** Fetches tenant templates with only agency-safe metadata and item counts. */
export const usePortalTemplatesQuery = () =>
  useQuery({
    queryKey: ["agent-portal", "templates"],
    queryFn: async () =>
      (await portalRequest<{ items: PortalTemplateSummary[] }>({ url: "/api/agent-portal/templates", method: "get" }))
        .data.items,
    staleTime: 60_000,
  });

/**
 * Fetches ranked shared-template plans for either a country route or a complete city route.
 * @param totalNights Derived itinerary night count
 * @param route Ordered countries, optionally enriched with city stops and night allocations
 * @param enabled Whether the route is ready for recommendations
 */
export const usePortalTemplateRecommendationsQuery = (
  totalNights: number,
  route: PortalRecommendationRouteStop[],
  enabled = true,
) =>
  useQuery({
    queryKey: ["agent-portal", "template-recommendations", totalNights, route],
    queryFn: async () =>
      (
        await portalRequest<PortalTemplateRecommendationResult>({
          url: "/api/agent-portal/templates/recommendations",
          method: "post",
          data: { totalNights, route },
        })
      ).data,
    enabled: enabled && totalNights > 0 && route.length > 0,
    staleTime: 30_000,
  });

/** Fetches one agency-safe template detail for the preview modal. */
export const usePortalTemplateQuery = (id?: string) =>
  useQuery({
    queryKey: ["agent-portal", "templates", id],
    queryFn: async () =>
      (await portalRequest<PortalTemplateDetail>({ url: `/api/agent-portal/templates/${id}`, method: "get" })).data,
    enabled: Boolean(id),
    staleTime: 60_000,
  });

/** Resolves selected templates into a cost-free agent-portal builder draft. */
export const useApplyPortalTemplatesMutation = () =>
  useMutation({
    mutationFn: async (application: PortalTemplateApplication) =>
      (
        await portalRequest<PortalTemplatePlan>({
          url: "/api/agent-portal/templates/apply",
          method: "post",
          data: application,
        })
      ).data,
  });

/** Provides a mutation that returns only the final agency-facing itinerary total. */
export const usePortalTotalPriceMutation = () =>
  useMutation({
    mutationFn: async ({ payload, itineraryId }: { payload: PortalItineraryPayload; itineraryId?: string }) =>
      (
        await portalRequest<{
          /** False when none of the chosen services could be priced. */
          priced?: boolean;
          totalPrice: { amount: number; currency: string };
          childCosts?: ChildCost[];
          warnings?: string[];
          supplierPassengers?: Array<{ key: string; quantity: number; amount: number; supplierTax: number }>;
          ageSelections?: PortalItineraryPayload["days"];
          /** Supplier per-person prices in the selling currency. */
          perPerson?: { adult: number; cwb: number; cnb: number; infant: number };
        }>({
          url: "/api/agent-portal/itineraries/quote-preview",
          method: "post",
          data: { itinerary: payload, itineraryId },
        })
      ).data,
  });

/**
 * Fetches the agency-wide portal itinerary list.
 * @param filters Server-side list filters
 */
export const usePortalItinerariesQuery = (filters: PortalItineraryListFilters) =>
  useQuery({
    queryKey: portalItineraryKeys.list(filters),
    queryFn: async () =>
      (
        await portalRequest<{ items: PortalItineraryListItem[]; pagination: PortalPagination }>({
          url: "/api/agent-portal/itineraries",
          method: "get",
          params: filters,
        })
      ).data,
    staleTime: 30_000,
  });

/** Fetches distinct agency trip requests with server-side filters. */
export const usePortalTripRequestsQuery = (filters: PortalTripRequestFilters) =>
  useQuery({
    queryKey: portalTripRequestKeys.list(filters),
    queryFn: async () => (await portalRequest<{ items: PortalTripRequestSummary[]; pagination: PortalPagination }>({
      url: "/api/agent-portal/leads",
      method: "get",
      params: filters,
    })).data,
    staleTime: 30_000,
  });

/** Fetches request-level dashboard totals and recent work. */
export const usePortalTripRequestDashboardQuery = () =>
  useQuery({
    queryKey: portalTripRequestKeys.dashboard(),
    queryFn: async () => (await portalRequest<PortalTripRequestDashboard>({
      url: "/api/agent-portal/leads/dashboard",
      method: "get",
    })).data,
    staleTime: 30_000,
  });

/** Fetches one trip request and all itinerary options. */
export const usePortalTripRequestQuery = (leadId?: string) =>
  useQuery({
    queryKey: portalTripRequestKeys.detail(leadId || ""),
    queryFn: async () => (await portalRequest<PortalTripRequestDetail>({
      url: `/api/agent-portal/leads/${leadId}`,
      method: "get",
    })).data,
    enabled: Boolean(leadId),
    staleTime: 30_000,
  });

/** Updates the optional guest name on an agency-owned request. */
export const useUpdatePortalTripRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, guestName }: { leadId: string; guestName: string }) =>
      (await portalRequest<{ id: string; guestName: string }>({
        url: `/api/agent-portal/leads/${leadId}`,
        method: "patch",
        data: { guestName },
      })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: portalTripRequestKeys.all });
      queryClient.invalidateQueries({ queryKey: portalTripRequestKeys.detail(variables.leadId) });
      queryClient.invalidateQueries({ queryKey: portalItineraryKeys.lists() });
    },
  });
};

/** Soft-cancels one editable trip request. */
export const useDiscardPortalTripRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (leadId: string) => (await portalRequest<{ id: string; status: "cancelled" }>({
      url: `/api/agent-portal/leads/${leadId}/discard`,
      method: "post",
    })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalTripRequestKeys.all });
      queryClient.invalidateQueries({ queryKey: portalItineraryKeys.all });
    },
  });
};

/** Soft-cancels an unwanted editable itinerary option. */
export const useDiscardPortalItineraryMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ itineraryId }: { itineraryId: string; leadId: string }) =>
      (await portalRequest<{ id: string; status: "cancelled" }>({
        url: `/api/agent-portal/itineraries/${itineraryId}/discard`,
        method: "post",
      })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalTripRequestKeys.all });
      queryClient.invalidateQueries({ queryKey: portalItineraryKeys.all });
    },
  });
};

/** Creates a new validated draft or updates an existing editable draft. */
export const useSavePortalItineraryDraftMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ itineraryId, leadId, guestName, payload }: { itineraryId?: string; leadId?: string; guestName?: string; payload: PortalItineraryPayload }) =>
      (
        await portalRequest<PortalItinerarySubmission>({
          url: itineraryId ? `/api/agent-portal/itineraries/${itineraryId}/draft` : "/api/agent-portal/itineraries",
          method: itineraryId ? "put" : "post",
          data: itineraryId ? { itinerary: payload } : { leadId, lead: leadId ? undefined : { guestName }, itinerary: payload },
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalItineraryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: portalTripRequestKeys.all });
    },
  });
};

/** Hands one saved draft to tenant staff. */
export const useSubmitPortalItineraryMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itineraryId: string) =>
      (await portalRequest<PortalItinerarySubmission>({ url: `/api/agent-portal/itineraries/${itineraryId}/submit`, method: "post" })).data,
    onSuccess: (_data, itineraryId) => {
      queryClient.invalidateQueries({ queryKey: portalItineraryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: portalItineraryKeys.detail(itineraryId) });
      queryClient.invalidateQueries({ queryKey: portalTripRequestKeys.all });
    },
  });
};

/** Loads the safe builder payload for an editable draft. */
export const usePortalItineraryDraftQuery = (itineraryId?: string) =>
  useQuery({
    queryKey: ["agent-portal", "itinerary-draft", itineraryId],
    queryFn: async () => (await portalRequest<{ id: string; leadId: string; reference: string; guestName: string; itinerary: PortalItineraryPayload }>({
      url: `/api/agent-portal/itineraries/${itineraryId}/draft`, method: "get",
    })).data,
    enabled: Boolean(itineraryId),
  });

/** Duplicates a saved option beneath the same draft lead. */
export const useDuplicatePortalItineraryMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itineraryId: string) => (await portalRequest<PortalItinerarySubmission>({
      url: `/api/agent-portal/itineraries/${itineraryId}/duplicate`, method: "post",
    })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalItineraryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: portalTripRequestKeys.all });
    },
  });
};

/**
 * Fetches one portal-safe itinerary detail.
 * @param itineraryId Itinerary UUID
 */
export const usePortalItineraryDetailQuery = (itineraryId: string) =>
  useQuery({
    queryKey: portalItineraryKeys.detail(itineraryId),
    queryFn: async () =>
      (
        await portalRequest<PortalItineraryDetail>({
          url: `/api/agent-portal/itineraries/${itineraryId}`,
          method: "get",
        })
      ).data,
    enabled: Boolean(itineraryId),
    staleTime: 30_000,
  });

/**
 * Polls the compact safe status endpoint while a request is still active.
 * @param itineraryId Itinerary UUID
 */
export const usePortalItineraryStatusQuery = (itineraryId: string) =>
  useQuery({
    queryKey: portalItineraryKeys.status(itineraryId),
    queryFn: async () =>
      (
        await portalRequest<{
          id: string;
          requestStatus: PortalStatus<PortalOptionStatus>;
          bookingStatus: PortalStatus<PortalBookingStatus>;
          totalPrice: { amount: number; currency: string };
          updatedAt: string | null;
          downloadAvailable: boolean;
        }>({
          url: `/api/agent-portal/itineraries/${itineraryId}/status`,
          method: "get",
        })
      ).data,
    enabled: Boolean(itineraryId),
    refetchInterval: 30_000,
  });

/** An agency's private selling markup on one quote. */
export type PortalQuoteMarkup = {
  markupBasis?: "taxExclusive" | "taxInclusive";
  taxBase?: "resale" | "markup" | "taxInclusiveResale";
  optionalPolicies?: Record<string, Omit<PortalQuoteMarkup, "optionalPolicies">>;
  markupType: "percentage" | "fixed";
  markupValue: number;
  /** GST the agent charges on the marked-up price; 0 means none. */
  gstPercent: number;
};

/** One line of the quote pricing table: the supplier price, the agent's markup and the client price. */
export type PortalQuotePricingRow = {
  key: "perAdult" | "perChildWithBed" | "perChildWithoutBed" | "perInfant" | "package" | "taxes" | "total" | `child:${string}` | "rounding" | `optional:${string}`;
  label: string;
  quantity?: number;
  yourPrice: number;
  markup: number;
  clientPrice: number;
};

export type PortalQuoteMarkupResponse = {
  markup: PortalQuoteMarkup | null;
  pricing: { currency: string; hasMarkup: boolean; rows: PortalQuotePricingRow[]; supplierPassengers?: Array<{ key: string; quantity: number; amount: number; supplierTax: number }> };
};

/**
 * Loads the agency's private markup and the pricing table for a saved quote.
 * @param itineraryId Itinerary UUID; the query is disabled until a quote exists
 */
export const usePortalQuoteMarkupQuery = (itineraryId?: string) =>
  useQuery({
    queryKey: ["agent-portal", "quote-markup", itineraryId],
    queryFn: async () =>
      (
        await portalRequest<PortalQuoteMarkupResponse>({
          url: `/api/agent-portal/itineraries/${itineraryId}/markup`,
          method: "get",
        })
      ).data,
    enabled: Boolean(itineraryId),
    staleTime: 30_000,
  });

/** Saves, replaces or (with `markup: null`) clears the agency's private markup on a quote. */
export const useSavePortalQuoteMarkupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ itineraryId, markup }: { itineraryId: string; markup: PortalQuoteMarkup | null }) =>
      (
        await portalRequest<PortalQuoteMarkupResponse>({
          url: `/api/agent-portal/itineraries/${itineraryId}/markup`,
          method: "put",
          data: markup ?? {},
        })
      ).data,
    onSuccess: (data, { itineraryId }) => {
      queryClient.setQueryData(["agent-portal", "quote-markup", itineraryId], data);
    },
  });
};

/**
 * Downloads a saved agency itinerary document from the portal-scoped endpoint.
 * @param itineraryId Submitted itinerary UUID
 */
export const downloadPortalItinerary = async (
  itineraryId: string,
  options: {
    includeImages: boolean;
    pickupAt?: string;
    dropOffAt?: string;
    /** `docx` downloads a Word document; omitted uses the tenant's default format. */
    format?: "pdf" | "docx";
    /** Adds a separate cost breakdown table to Word documents (ignored for PDFs). */
    includeCostBreakdown?: boolean;
  },
) =>
  portalRequest<Blob>({
    url: `/api/agent-portal/itineraries/${itineraryId}/download`,
    method: "get",
    params: {
      includeImages: options.includeImages ? "true" : "false",
      pickupAt: options.pickupAt,
      dropOffAt: options.dropOffAt,
      format: options.format,
      includeCostBreakdown: options.includeCostBreakdown ? "true" : undefined,
    },
    responseType: "blob",
  });
